import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  COMPANY_ADDRESS_LINE_1,
  COMPANY_ADDRESS_LINE_2,
  COMPANY_EMAIL,
  COMPANY_EMAIL_HREF,
  COMPANY_GOOGLE_MAPS_URL,
  COMPANY_MAP_EMBED_URL,
  COMPANY_PHONE_DISPLAY,
  COMPANY_PHONE_TEL,
} from '../constants/companyContact';
import { apiUrl } from '../lib/apiUrl';

const inputCls =
  'mt-1.5 w-full rounded-xl border border-white/12 bg-white/[0.07] px-4 py-2.5 font-body text-sm text-[var(--home-text-on-dark)] placeholder:text-[var(--home-muted-on-dark)] shadow-inner outline-none transition focus:border-[var(--home-miami-blue)] focus:ring-2 focus:ring-[var(--home-miami-blue)]/35';

/** Contact form persists nothing server-side in this reference build — integrate Resend/SendGrid in production. */

export function Contact() {
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const full_name = String(fd.get('full_name') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const message = String(fd.get('message') ?? '').trim();
    setPending(true);
    try {
      const res = await fetch(apiUrl('/api/public/leads'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'contact',
          full_name: full_name || undefined,
          email,
          message,
          meta: {},
        }),
      });
      if (!res.ok) throw new Error('submit');
      setSent(true);
    } catch {
      setErr('Could not send — try email or phone instead.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative mx-auto max-w-[90rem] scroll-mt-8 px-4 pb-24 pt-16 sm:pt-20 md:px-8 lg:px-10 lg:pt-24">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:items-start lg:gap-14">
        <div className="space-y-5">
          <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/40 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04]">
            <div className="aspect-[4/3] min-h-[220px] w-full lg:min-h-[280px]">
              <iframe
                title="Map — Breckenridge Capital, Tampa"
                src={COMPANY_MAP_EMBED_URL}
                className="h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
          <address className="not-italic rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/40 px-5 py-4 font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)] ring-1 ring-white/[0.04] backdrop-blur-sm">
            <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[var(--header-shell-bg)]">
              Office
            </p>
            <p className="mt-2 text-[var(--home-text-on-dark)]">
              {COMPANY_ADDRESS_LINE_1}
              <br />
              {COMPANY_ADDRESS_LINE_2}
            </p>
            <p className="mt-3">
              <a href={COMPANY_PHONE_TEL} className="font-medium text-[var(--home-miami-blue)] hover:underline">
                {COMPANY_PHONE_DISPLAY}
              </a>
            </p>
            <p className="mt-1">
              <a href={COMPANY_EMAIL_HREF} className="font-medium text-[var(--home-text-on-dark)] hover:text-[var(--home-miami-blue)]">
                {COMPANY_EMAIL}
              </a>
            </p>
            <p className="mt-4">
              <a
                href={COMPANY_GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-display text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-[var(--home-miami-blue)] hover:underline"
              >
                Open in Google Maps
              </a>
            </p>
          </address>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm md:p-8">
          <div>
            <h1 className="font-display text-2xl font-bold uppercase tracking-[0.06em] text-[var(--home-text-on-dark)] md:text-3xl">
              Contact investor relations
            </h1>
            <p className="mt-4 font-body text-[0.95rem] leading-relaxed text-[var(--home-muted-on-dark)] md:text-base">
              For onboarding, accreditation questions, IRA structures, or data room access, submit the form below. In production,
              wire this submission to Zendesk, HubSpot, Salesforce, or a secure CRM queue.
            </p>
          </div>

          {sent ? (
            <div className="mt-8 rounded-2xl border border-emerald-500/40 bg-emerald-950/30 px-4 py-6 font-body text-sm leading-relaxed text-emerald-200">
              Thank you — your concierge will respond within two business days.
            </div>
          ) : (
            <form className="mt-8 space-y-5" onSubmit={(ev) => void onSubmit(ev)}>
              <label className="flex flex-col font-display text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/90">
                Name
                <input name="full_name" type="text" autoComplete="name" className={inputCls} placeholder="Your name" />
              </label>
              <label className="flex flex-col font-display text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/90">
                Email
                <input name="email" required type="email" autoComplete="email" className={inputCls} placeholder={COMPANY_EMAIL} />
              </label>
              <label className="flex flex-col font-display text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/90">
                Message
                <textarea name="message" required rows={5} className={inputCls} placeholder="How can we help?" />
              </label>
              {err ? <p className="text-sm text-rose-300">{err}</p> : null}
              <button
                type="submit"
                disabled={pending}
                className="w-full rounded-full bg-[var(--header-shell-bg)] px-8 py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow-[0_8px_28px_-8px_rgb(244_114_182/0.45)] ring-1 ring-white/20 transition hover:brightness-110 disabled:opacity-60 md:w-auto"
              >
                {pending ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
